import React, { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { Button, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listNotifications } from '../api/notifications.api';
import { useNotifications } from '../hooks/useNotifications';
import type { NotificationItem } from '../types';

export const CriticalNotificationBanner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasCriticalUnread, markRead, version } = useNotifications();
  const [item, setItem] = useState<NotificationItem | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (!hasCriticalUnread) {
      setItem(null);
      return undefined;
    }

    listNotifications({ filter: 'unread', limit: 20 })
      .then((data) => {
        if (!cancelled) {
          setItem(data.items.find((notification) => notification.severity === 'CRITICAL') ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItem(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [hasCriticalUnread, version]);

  if (!item) {
    return null;
  }

  return (
    <div className="flex min-h-11 items-center gap-3 border-b border-red-200 bg-red-50 px-4 py-2 text-red-900 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-100 md:px-6 lg:px-8">
      <AlertTriangle size={18} className="shrink-0" />
      <Typography className="min-w-0 flex-1 truncate text-sm font-medium">{item.title}</Typography>
      <Button
        size="small"
        variant="contained"
        color="error"
        onClick={() => navigate(`/notifications/${item.publicId}`)}
        className="shrink-0 normal-case"
      >
        {t('notifications.banner.view')}
      </Button>
      <IconButton
        size="small"
        aria-label={t('notifications.banner.dismiss')}
        onClick={() => void markRead(item.publicId)}
        className="shrink-0 text-red-800 dark:text-red-100"
      >
        <X size={18} />
      </IconButton>
    </div>
  );
};
